import { useEffect, useState } from "react";
import { ShopLinkCard } from "../components/ShopLinkCard";
import { toast } from "sonner";
import { Copy, Eye, EyeOff, Send, RefreshCw, Link2, Loader2, FlaskConical } from "lucide-react";
import { http, errMsg } from "../lib/api";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copied"); };

const WebhookCard = ({ cfg }) => {
  const [show, setShow] = useState(false);
  const sample = JSON.stringify({ order_id: "WC-10023", product_slug: "<product slug>", customer_email: "buyer@example.com", customer_name: "Jane" }, null, 2);
  return (
    <div className="rounded-xl border border-stone-200 bg-white p-6" data-testid="webhook-settings">
      <div className="eyebrow">Connect pbtravelangel.com checkout</div>
      <p className="text-sm text-stone-500 mt-3">After a successful Stripe or PayPal payment on pbtravelangel.com, send this request. The buyer is emailed the PDF + interactive app, and the response includes a <code className="text-[#9A7B2C]">download_url</code> you can redirect them to.</p>
      <div className="mt-4 space-y-3 text-xs font-mono-ui">
        <div><span className="text-stone-500">POST </span><span data-testid="webhook-url" className="text-[#9A7B2C] break-all">{cfg.webhook_url}</span>
          <button onClick={() => copy(cfg.webhook_url)} className="ml-2 text-stone-500 hover:text-stone-900"><Copy size={12} /></button></div>
        <div className="flex items-center gap-2"><span className="text-stone-500">Header X-Fulfilment-Key:</span>
          <span data-testid="webhook-key" className="text-stone-200 break-all">{show ? cfg.api_key : "•".repeat(24)}</span>
          <button data-testid="webhook-key-toggle" onClick={() => setShow(!show)} className="text-stone-500 hover:text-stone-900">{show ? <EyeOff size={12} /> : <Eye size={12} />}</button>
          <button data-testid="webhook-key-copy" onClick={() => copy(cfg.api_key)} className="text-stone-500 hover:text-stone-900"><Copy size={12} /></button></div>
        <pre className="bg-[#F7F5F0] rounded-lg p-3 text-stone-600 overflow-auto">{sample}</pre>
        <div className="text-stone-500">Emails send as “{cfg.from_name}” · replies go to {cfg.reply_to}</div>
      </div>
    </div>
  );
};

const ManualOrder = ({ products, onDone }) => {
  const [f, setF] = useState({ product_slug: "", customer_email: "", customer_name: "", order_id: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await http.post("/orders", f, { timeout: 180000 });
      toast[data.email_status === "sent" ? "success" : "error"](data.email_status === "sent" ? `Kit emailed to ${data.customer_email}` : `Email failed: ${data.email_error}`);
      setF({ ...f, customer_email: "", customer_name: "", order_id: "" });
      onDone();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="rounded-xl border border-stone-200 bg-white p-6 space-y-3" data-testid="manual-order-form">
      <div className="eyebrow">Send a kit manually</div>
      <Select value={f.product_slug} onValueChange={(v) => setF({ ...f, product_slug: v })}>
        <SelectTrigger data-testid="manual-order-product" className="bg-[#F7F5F0] h-11"><SelectValue placeholder="Published product" /></SelectTrigger>
        <SelectContent>{products.map((p) => <SelectItem key={p.slug} value={p.slug}>{p.title}</SelectItem>)}</SelectContent>
      </Select>
      <Input data-testid="manual-order-email" type="email" required placeholder="Buyer email" value={f.customer_email} onChange={(e) => setF({ ...f, customer_email: e.target.value })} className="bg-[#F7F5F0] h-11" />
      <div className="grid grid-cols-2 gap-3">
        <Input data-testid="manual-order-name" placeholder="Buyer name" value={f.customer_name} onChange={(e) => setF({ ...f, customer_name: e.target.value })} className="bg-[#F7F5F0] h-11" />
        <Input data-testid="manual-order-id" placeholder="Order ID (optional)" value={f.order_id} onChange={(e) => setF({ ...f, order_id: e.target.value })} className="bg-[#F7F5F0] h-11" />
      </div>
      <button data-testid="manual-order-submit" disabled={busy || !f.product_slug} className="inline-flex items-center gap-2 rounded-sm bg-[#C9A847] text-white px-5 py-2.5 text-sm font-bold hover:bg-[#B8973A] disabled:opacity-50">
        <Send size={15} /> {busy ? "Building & sending…" : "Email kit to buyer"}
      </button>
    </form>
  );
};

const TestOrder = ({ products, adminEmail, onDone }) => {
  const [slug, setSlug] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await http.post("/orders/test", { product_slug: slug, customer_email: email }, { timeout: 280000 });
      if (data.email_status === "sent") toast.success(`Test order ${data.order_id} emailed to ${data.customer_email}`, { action: { label: "Open download page", onClick: () => window.open(data.download_url, "_blank") } });
      else toast.error(`Email failed: ${data.email_error}`);
      onDone();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border border-[#C9A847]/40 bg-[#FAF7F0] p-6 space-y-3" data-testid="test-order-card">
      <div className="eyebrow">Send me a test order</div>
      <p className="text-sm text-stone-500">Runs the exact delivery a paying buyer gets — email with the PDF + app and a private download page. Marked TEST and kept out of buyer counts and update emails.</p>
      <Select value={slug} onValueChange={setSlug}>
        <SelectTrigger data-testid="test-order-product" className="bg-white h-11"><SelectValue placeholder="Pick a published product" /></SelectTrigger>
        <SelectContent>{products.map((p) => <SelectItem key={p.slug} value={p.slug}>{p.title}</SelectItem>)}</SelectContent>
      </Select>
      <Input data-testid="test-order-email" type="email" placeholder={adminEmail || "Your email"} value={email} onChange={(e) => setEmail(e.target.value)} className="bg-white h-11" />
      <button data-testid="test-order-submit" disabled={busy || !slug} onClick={send} className="inline-flex items-center gap-2 rounded-sm bg-[#2B2A26] text-white px-5 py-2.5 text-sm font-bold hover:bg-black disabled:opacity-50 transition-colors">
        {busy ? <Loader2 size={15} className="animate-spin" /> : <FlaskConical size={15} />} {busy ? "Building & sending…" : "Send me a test order"}
      </button>
    </div>
  );
};

const OrderRow = ({ o, onDone }) => {
  const [busy, setBusy] = useState(false);
  const resend = async () => {
    setBusy(true);
    try { const { data } = await http.post(`/orders/${o.id}/resend`, {}, { timeout: 180000 }); toast[data.email_status === "sent" ? "success" : "error"](data.email_status === "sent" ? "Resent" : data.email_error); onDone(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <tr data-testid={`order-row-${o.order_id}`} className="border-b border-stone-200 text-sm">
      <td className="p-3 font-mono-ui text-xs text-[#9A7B2C]">{o.order_id}{o.test && <span data-testid={`order-test-badge-${o.order_id}`} className="ml-2 rounded-sm bg-sky-50 border border-sky-200 px-1.5 py-0.5 text-[10px] font-bold text-sky-700">TEST</span>}<div className="text-stone-600">{o.source}</div></td>
      <td className="p-3"><div className="text-[#2B2A26]">{o.customer_name || "—"}</div><div className="text-xs text-stone-500">{o.customer_email}</div></td>
      <td className="p-3 text-xs text-stone-500">{o.product_slug}</td>
      <td className="p-3 text-xs">
        <span data-testid={`order-email-status-${o.order_id}`} className={o.email_status === "sent" ? "text-emerald-400" : "text-red-400"}>{o.email_status}</span>
        {o.email_mode && <div className="text-stone-500">{o.email_mode === "attachments" ? "files attached" : "links only"}</div>}
        {o.email_error && <div className="text-red-400/70 max-w-[200px] truncate">{o.email_error}</div>}
      </td>
      <td className="p-3 text-xs text-stone-500 font-mono-ui">PDF {o.downloads?.pdf || 0} · App {o.downloads?.app || 0}</td>
      <td className="p-3"><div className="flex gap-2 justify-end">
        <button data-testid={`order-copy-link-${o.order_id}`} onClick={() => copy(o.download_url)} className="p-2 rounded-sm border border-stone-300 text-stone-500 hover:text-stone-900"><Link2 size={13} /></button>
        <button data-testid={`order-resend-${o.order_id}`} disabled={busy} onClick={resend} className="p-2 rounded-sm border border-stone-300 text-stone-500 hover:text-stone-900"><RefreshCw size={13} className={busy ? "animate-spin" : ""} /></button>
      </div></td>
    </tr>
  );
};

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [cfg, setCfg] = useState(null);
  const [products, setProducts] = useState([]);
  const load = () => http.get("/orders").then((r) => setOrders(r.data));
  useEffect(() => {
    load();
    http.get("/settings/fulfilment").then((r) => setCfg(r.data));
    http.get("/public/products").then((r) => setProducts(r.data));
  }, []);
  return (
    <div className="p-8 lg:p-12">
      <div className="eyebrow rise">Fulfilment</div>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#2B2A26] mt-2 mb-10 rise">Orders & Delivery</h1>
      <div className="grid lg:grid-cols-2 gap-6 mb-10">
        {cfg && <WebhookCard cfg={cfg} />}
        <ManualOrder products={products} onDone={load} />
        <TestOrder products={products} adminEmail={cfg?.admin_email} onDone={load} />
        <ShopLinkCard />
      </div>
      <div data-testid="orders-counts" className="mb-3 text-sm text-stone-500">{orders.filter((o) => !o.test).length} real order(s) · {orders.filter((o) => o.test).length} test order(s)</div>
      <div className="rounded-xl border border-stone-200 bg-white overflow-auto">
        <table className="w-full">
          <thead><tr className="text-left eyebrow"><th className="p-3">Order</th><th className="p-3">Buyer</th><th className="p-3">Product</th><th className="p-3">Email</th><th className="p-3">Downloads</th><th className="p-3" /></tr></thead>
          <tbody>{orders.map((o) => <OrderRow key={o.id} o={o} onDone={load} />)}</tbody>
        </table>
        {orders.length === 0 && <div data-testid="orders-empty" className="p-10 text-center text-stone-500">No orders yet.</div>}
      </div>
    </div>
  );
}
